import { useEffect } from 'react'

import { DEFAULT_THEME, useUiStore, type Theme } from '@/stores/uiStore'

/**
 * Temaet lever ét sted: attributten `data-theme` på <html>.
 *
 * Alle farver er tokens i index.css, og `[data-theme='light']` overskriver
 * dem. Derfor er der ingen `dark:`-varianter i komponenterne — og derfor er
 * det her den eneste kode, der overhovedet rører temaet.
 *
 * Attributten sættes tre steder, og de tre skal være enige:
 *   1. statisk i index.html, så en side uden JavaScript stadig er lys,
 *   2. af et lille script før første maling, så et gemt valg af mørkt ikke
 *      blinker hvidt først,
 *   3. herfra, når brugeren skifter.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme
}

/** Holder <html> i sync med storen. Kaldes ét sted, øverst i App. */
export function useApplyTheme(): void {
  const theme = useUiStore((state) => state.theme)

  useEffect(() => {
    applyTheme(theme)
  }, [theme])
}

/**
 * Samme opslag som scriptet i index.html laver. Ligger her, så nøglen og
 * formen på det gemte objekt kun er beskrevet ét sted i TypeScript — scriptet
 * i index.html kan ikke importere noget, men det kan holdes ordret magen til.
 */
export function readStoredTheme(storage: Pick<Storage, 'getItem'>): Theme {
  try {
    const raw = storage.getItem('bygoverblik-ui')
    if (!raw) return DEFAULT_THEME

    const parsed: unknown = JSON.parse(raw)
    const theme = (parsed as { state?: { theme?: unknown } } | null)?.state?.theme

    return theme === 'dark' || theme === 'light' ? theme : DEFAULT_THEME
  } catch {
    // Ødelagt eller utilgængelig localStorage må ikke stoppe opstarten.
    return DEFAULT_THEME
  }
}
